import React, { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { MotiView } from "moti";
import AdminLayout from "../../layouts/AdminLayout.web";
import { colors, radii, shadowStyles, spacing, typographyScale, typographyStyles } from "../../styles/design-system";
import {
  CourseSessionAnalyticsInput,
  TopQuestionScopeFilter,
  useGetCourseSessionAnalyticsQuery,
  useGetFacultyDashboardStatsQuery,
  useGetFacultyTopQuestionsQuery,
  useGetCoursesByFacultyQuery,
} from "@axio/graphql/generated/graphql";
import CommonQuestionsTab from "../../components/dashboard/Tabs/CommonQuestionsTab";
import AssignmentSupportTab from "../../components/dashboard/Tabs/AssignmentSupportTab";
import FacultyStatsCards from "../../components/dashboard/Faculty/FacultyStatsCards";
import { RobotIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import CustomDropdown, { CustomDropdownOption } from "../../components/common/general/CustomDropDown/CustomDropdown";
import { useAppConfig } from "../../AppConfigProvider";

import {
  CellText,
  CellTextBold,
  Column,
  DataTable,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/adminDashboard/AICompanion";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { formatTimeAgo } from "../../utils/date";

type DashboardTab = "common" | "assignment" | "activity";

const PAGE_SIZE = 20;

function FacultyDashboardCompanionView() {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshRotation, setRefreshRotation] = useState(0);

  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<DashboardTab>("common");
  const [page, setPage] = useState<number>(1);
  const [scope, setScope] = useState<TopQuestionScopeFilter>(TopQuestionScopeFilter.All);

  const { userInfo } = useContext(AuthContext);
  const facultyId = userInfo?.userId;
  const appConfig = useAppConfig();
  const schoolId = appConfig?.axioSchools[0];

  const { data: courseAnalyticsData, refetch: refetchCourseAnalytics } = useGetCourseSessionAnalyticsQuery({
    variables: {
      input: {
        startDate: undefined,
        endDate: undefined,
        limit: 5,
        facultyId: facultyId,
      } as CourseSessionAnalyticsInput,
    },
    skip: !facultyId,
  });

  const { data: statsData, refetch: refetchStats } = useGetFacultyDashboardStatsQuery({
    skip: !facultyId,
  });

  const handleTabChange = (value: string) => {
    if (value === "common" || value === "assignment" || value === "activity") {
      setSelectedTab(value as DashboardTab);
    }
  };

  const handleScopeChange = (newScope: TopQuestionScopeFilter) => {
    setScope(newScope);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  const {
    data: facultyTopQuestionsData,
    loading: facultyTopQuestionsLoading,
    refetch: refetchFacultyTopQuestions,
  } = useGetFacultyTopQuestionsQuery({
    variables: { courseId: selectedCourseId, page, pageSize: PAGE_SIZE, scope },
    skip: !facultyId,
  });

  const { data: coursesData } = useGetCoursesByFacultyQuery({
    variables: {
      schoolId,
      userId: facultyId ?? "",
      searchQuery: "",
    },
    skip: !schoolId || !facultyId,
  });

  const topQuestions = facultyTopQuestionsData?.getFacultyTopQuestions?.data ?? [];

  const courseOptions = useMemo(() => {
    const courses = coursesData?.getCoursesByFaculty?.data ?? [];
    return courses.map((course) => ({ id: course.id, name: course.name }));
  }, [coursesData]);

  const courseDropdownOptions = useMemo<CustomDropdownOption[]>(() => {
    const options = courseOptions.map((course) => ({ label: course.name, value: course.id }));
    return [{ label: "All Courses", value: "all" }, { label: "General", value: "GENERAL" }, ...options];
  }, [courseOptions]);

  const totalCount = facultyTopQuestionsData?.getFacultyTopQuestions?.totalCount ?? 0;
  const assignmentCount = facultyTopQuestionsData?.getFacultyTopQuestions?.assignmentCount ?? 0;
  const courseCount = facultyTopQuestionsData?.getFacultyTopQuestions?.courseCount ?? 0;
  const assignmentQuestionCount = facultyTopQuestionsData?.getFacultyTopQuestions?.assignmentQuestionCount ?? 0;
  const courseQuestionCount = facultyTopQuestionsData?.getFacultyTopQuestions?.courseQuestionCount ?? 0;

  React.useEffect(() => {
    setPage(1);
    setScope(TopQuestionScopeFilter.All);
  }, [selectedCourseId]);

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshRotation((prev) => prev + 360);

    try {
      await Promise.all([refetchCourseAnalytics(), refetchStats()]);
      setLastUpdated(new Date()); // update timestamp
    } catch (error) {
      console.error("Error refreshing faculty analytics:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  type ActiveStudent = {
    id: string;
    name: string;
    initials: string;
    topTopic: string;
    sessions: number;
    lastActive: string;
  };

  const activeStudents = useMemo<ActiveStudent[]>(() => {
    const users = courseAnalyticsData?.getCourseSessionAnalytics?.topActiveUsers;

    if (!users) return [];

    return users.map((user) => {
      const initials = user.name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

      return {
        id: user.id,
        name: user.name,
        initials,
        topTopic: "-", // We don’t have this yet
        sessions: user.totalSessions,
        lastActive: "", // We can compute later if needed
      };
    });
  }, [courseAnalyticsData]);

  const activeStudentColumns: Column<ActiveStudent>[] = [
    {
      key: "student",
      flex: 3,
      renderCell: (student) => (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
          {/* Avatar */}
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: "#E0E7FF",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Text style={{ color: "#3E68FE", fontWeight: "600" }}>{student.initials}</Text>
          </View>

          {/* Name + topic */}
          <View>
            <CellTextBold>{student.name}</CellTextBold>
            {/* do not delete the next line, we might use it later */}
            {/* <CellText style={{ color: "#667085" }}>Top topic: {student.topTopic}</CellText> */}
          </View>
        </View>
      ),
    },
    {
      key: "activity",
      flex: 1,
      textAlign: "right",
      alignItems: "flex-end",
      renderCell: (student) => (
        <View style={{ alignItems: "flex-end" }}>
          <CellTextBold>{`${student.sessions} sessions`}</CellTextBold>
          <CellText style={{ color: "#667085" }}>{student.lastActive}</CellText>
        </View>
      ),
    },
  ];

  const questionBreakdown = useMemo(
    () => ({
      assignment: assignmentQuestionCount,
      course: courseQuestionCount,
    }),
    [assignmentQuestionCount, courseQuestionCount]
  );

  return (
    <AdminLayout withPadding={false}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.container}>
        {/* Page Header */}
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          // @ts-expect-error - transition type not fully typed
          transition={{ type: "timing", duration: 500 }}
          style={styles.headerSection}>
          <View style={styles.headerContent}>
            <View>
              <View style={styles.titleRow}>
                <Text style={styles.title}>Dashboard</Text>
                <View style={styles.badge}>
                  <RobotIcon size={14} color={colors.purple[700]} weight="fill" />
                  <Text style={styles.badgeText}>AI Companion</Text>
                </View>
              </View>
              <Text style={styles.subtitle}>Monitor how your students are using AI Companion in your courses.</Text>
            </View>
            <View style={styles.headerActions}>
              <View style={styles.lastUpdated}>
                <Text style={styles.lastUpdatedLabel}>Last Updated</Text>
                <Text style={styles.lastUpdatedValue}>{formatTimeAgo(lastUpdated)}</Text>
              </View>
              <Pressable
                onPress={handleRefresh}
                disabled={isRefreshing}
                style={[styles.refreshButton, isRefreshing && styles.refreshButtonDisabled]}
                aria-label="Refresh data">
                <MotiView
                  animate={{ rotate: `${refreshRotation}deg` }}
                  transition={{
                    rotate: {
                      type: "timing",
                      duration: 1000,
                    },
                  }}>
                  <ArrowsClockwiseIcon size={20} color={colors.grey[600]} weight="regular" />
                </MotiView>
              </Pressable>
            </View>
          </View>
        </MotiView>

        {/* Stats Grid - 3 cards */}

        <FacultyStatsCards
          // Note: zeros here may indicate a backend fetch error (logged server-side as logger.error).
          // The query intentionally returns null on failure rather than throwing; we display zeros to avoid
          // breaking the dashboard. Check backend logs if a faculty reports persistent zero stats.
          totalUsers={statsData?.getFacultyDashboardStats?.totalUsers ?? 0}
          totalSessions={statsData?.getFacultyDashboardStats?.totalSessions ?? 0}
          avgSessionLength={
            Math.round(((statsData?.getFacultyDashboardStats?.avgSessionLength ?? 0) / 60_000) * 10) / 10
          }
          questionsAnswered={questionBreakdown.assignment + questionBreakdown.course}
          assignmentQuestions={questionBreakdown.assignment}
          courseQuestions={questionBreakdown.course}
        />

        <View style={styles.courseFilterSection}>
          <CustomDropdown
            options={courseDropdownOptions}
            value={selectedCourseId ?? "all"}
            onChange={(value) => setSelectedCourseId(value === "all" ? null : value)}
            placeholder="All Courses"
            fullWidth={false}
            containerStyle={styles.courseDropdown}
            accessibilityLabel="Select course"
            showArrow
          />
        </View>

        <Tabs value="common">
          <TabsContent value="common">
            <View style={[styles.card, shadowStyles.sm]}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Common Questions</Text>
                <Text style={styles.cardSubtitle}>{totalCount} questions</Text>
              </View>

              <CommonQuestionsTab
                questions={topQuestions}
                loading={facultyTopQuestionsLoading}
                allCount={assignmentCount + courseCount}
                assignmentCount={assignmentCount}
                courseCount={courseCount}
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                scope={scope}
                onScopeChange={handleScopeChange}
              />
            </View>
          </TabsContent>

          {/* Will be implemented in the future, but for now we are not showing this tab. */}
          {/* <TabsContent value="assignment">
            <View style={[styles.card, shadowStyles.sm]}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Assignment Support</Text>
                <Text style={styles.cardSubtitle}>{assignmentTabCount} assignments</Text>
              </View>
              <AssignmentSupportTab onCountChange={setAssignmentTabCount} />
            </View>
          </TabsContent> */}
        </Tabs>
      </ScrollView>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: colors.grey[50],
  },
  container: {
    maxWidth: 1280,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    // @ts-expect-error - minHeight works on web
    minHeight: "100vh",
    userSelect: "text",
  },
  headerSection: {
    marginBottom: spacing.xl,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: typographyScale.displayMd.fontSize,
    lineHeight: typographyScale.displayMd.lineHeight,
    fontWeight: typographyStyles.fontBold.fontWeight,
    color: colors.grey[900],
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: spacing.xxs,
    backgroundColor: colors.purple[100],
    borderRadius: radii.full,
  },
  badgeText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.purple[700],
  },
  subtitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    color: colors.grey[600],
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  courseFilterSection: {
    marginTop: spacing.lg,
    marginBottom: spacing.lg,
    alignItems: "flex-start",
  },
  lastUpdated: {
    alignItems: "flex-end",
  },
  lastUpdatedLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  lastUpdatedValue: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[700],
  },
  refreshButton: {
    width: spacing["2xl"],
    height: spacing["2xl"],
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    alignItems: "center",
    justifyContent: "center",
  },
  refreshButtonDisabled: {
    opacity: 0.5,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.lg,
  },
  cardTitle: {
    fontSize: typographyScale.textMd.fontSize,
    lineHeight: typographyScale.textMd.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[900],
  },
  courseDropdown: {
    minWidth: 180,
  },
  cardSubtitle: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[500],
  },
});

export default FacultyDashboardCompanionView;
